import type { QueryClient } from '@tanstack/react-query'
import { createNadDebug } from '../utils/debugLog'
import type { ProjectConfig, UpdateProjectConfigRequest } from './types'

const log = createNadDebug('dw:canvas-project-config:autosave')

interface PendingSave {
  sequence: number
  data: UpdateProjectConfigRequest
}
interface SaveQueue {
  confirmed: ProjectConfig | undefined
  pending: PendingSave[]
  tail: Promise<void>
  sequence: number
  traceId: string
  active: boolean
}

// Shared across hook instances, isolated across QueryClients and canvases. A
// queue only lives while writes are pending; the query cache owns idle state.
const queues = new WeakMap<QueryClient, Map<string, SaveQueue>>()

function applyPatch(config: ProjectConfig, patch: UpdateProjectConfigRequest): ProjectConfig {
  const { content_language, ...rest } = patch
  return {
    ...config,
    ...rest,
    ...(content_language === undefined ? {} : {
      content_language: content_language === 'auto' ? null : content_language,
    }),
  }
}

function optimisticConfig(queue: SaveQueue, fallback?: ProjectConfig): ProjectConfig | undefined {
  const confirmed = queue.confirmed ?? fallback
  return confirmed && queue.pending.reduce((config, save) => applyPatch(config, save.data), confirmed)
}

/** A broadcast/focus refetch must not erase edits whose PATCH is still pending. */
export function reconcileProjectConfigRead(
  client: QueryClient,
  key: string[],
  fetched: ProjectConfig,
): ProjectConfig {
  const queue = queues.get(client)?.get(JSON.stringify(key))
  if (queue) queue.confirmed ??= fetched
  const visible = queue ? optimisticConfig(queue, fetched) ?? fetched : fetched
  log({
    event: 'config-read:reconciled',
    project_id: key[1],
    canvas_id: key[2],
    occurred_at: new Date().toISOString(),
    pending_save_count: queue?.pending.length ?? 0,
    server_preferred_video_model_ids: JSON.stringify(fetched.preferred_video_models),
    visible_preferred_video_model_ids: JSON.stringify(visible.preferred_video_models),
  })
  return visible
}

export function saveProjectConfig(
  client: QueryClient,
  key: string[],
  projectId: string,
  canvasId: string,
  data: UpdateProjectConfigRequest,
  write: () => Promise<ProjectConfig>,
): Promise<ProjectConfig> {
  let clientQueues = queues.get(client)
  if (!clientQueues) {
    clientQueues = new Map()
    queues.set(client, clientQueues)
  }
  const queueKey = JSON.stringify(key)
  let existing = clientQueues.get(queueKey)
  if (!existing) {
    existing = { confirmed: client.getQueryData<ProjectConfig>(key), pending: [], tail: Promise.resolve(), sequence: 0, traceId: crypto.randomUUID(), active: false }
    clientQueues.set(queueKey, existing)
  }
  const queue = existing
  const save = { sequence: ++queue.sequence, data }
  queue.pending.push(save)
  // cancelQueries cancels/reverts synchronously; do not yield before publishing
  // the next optimistic snapshot, so the next click sees the latest list.
  void client.cancelQueries({ queryKey: key, exact: true })
  client.setQueryData<ProjectConfig>(key, optimisticConfig(queue))

  const trace = (event: string, error?: string) => log({
    event,
    project_id: projectId,
    canvas_id: canvasId,
    trace_id: queue.traceId,
    occurred_at: new Date().toISOString(),
    save_sequence: save.sequence,
    queued_save_count: queue.pending.length - Number(queue.active),
    in_flight_save_count: Number(queue.active),
    // NAD summarizes array values into counts. JSON strings preserve the model
    // identities and priority order needed to diagnose lost selections.
    changed_fields: Object.keys(data).join(','),
    requested_preferred_video_model_ids: JSON.stringify(data.preferred_video_models),
    confirmed_preferred_video_model_ids: JSON.stringify(queue.confirmed?.preferred_video_models),
    visible_preferred_video_model_ids: JSON.stringify(client.getQueryData<ProjectConfig>(key)?.preferred_video_models),
    ...(error === undefined ? {} : { error }),
  })
  trace('config-save:queued')
  const result = queue.tail.then(async () => {
    queue.active = true
    trace('config-save:started')
    let succeeded = false
    try {
      const saved = await write()
      queue.confirmed = saved
      succeeded = true
      trace('config-save:server_acknowledged')
      return saved
    } catch (error) {
      trace('config-save:failed', error instanceof Error ? error.message : String(error))
      throw error
    } finally {
      queue.pending.shift()
      queue.active = false
      // Discard any read started before this response, then replay only edits
      // still pending over the last successful server snapshot. Failed patches
      // are removed rather than restored by another mutation's rollback.
      void client.cancelQueries({ queryKey: key, exact: true })
      client.setQueryData<ProjectConfig>(key, optimisticConfig(queue))
      trace(succeeded ? 'config-save:ui_confirmed' : 'config-save:ui_rolled_back')
      if (queue.pending.length === 0) {
        clientQueues.delete(queueKey)
        trace('config-save:queue_empty')
        void client.invalidateQueries({ queryKey: key, exact: true })
      }
    }
  })
  // A failed request still rejects its caller (and triggers the existing toast),
  // but must not poison the queue or prevent later user edits from being saved.
  queue.tail = result.then(() => undefined, () => undefined)
  return result
}
